import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ensureDLToken } from "@/lib/dlToken";
import OrderForm from "@/components/OrderForm";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STASH_KEY = "scanRosterPrefill";

/**
 * /admin/orders/new — Wraps OrderForm for super admins / dispatchers.
 *
 * Query params:
 *  - ?prefill=<value>   → falls back to Notes when no roster row is found
 *  - ?autoprint=1       → on successful create, opens the 4×6 label PDF
 *  - ?from_roster=1     → indicates a sessionStorage stash holds the patient row
 */
export default function AdminOrderNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ordersHref = user?.role === "super_admin" ? "/admin/orders" : "/dispatch/orders";
  const [pharmacies, setPharmacies] = useState([]);
  const [searchParams] = useSearchParams();
  const prefill = searchParams.get("prefill") || "";
  const autoprint = searchParams.get("autoprint") === "1";
  const fromRoster = searchParams.get("from_roster") === "1";

  // Read the stashed roster row (if any) — sessionStorage so it survives refresh
  // of *this* tab but is naturally evicted when the tab closes.
  const roster = useMemo(() => {
    if (!fromRoster) return null;
    try {
      const raw = sessionStorage.getItem(STASH_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [fromRoster]);

  useEffect(() => {
    api.get("/pharmacies").then((r) => setPharmacies(r.data));
  }, []);

  const handleCreated = async (data) => {
    // Mark roster row consumed so it doesn't get reused
    if (roster?.order_number) {
      api.post(`/scan/roster/${encodeURIComponent(roster.order_number)}/consume`).catch(() => {});
      try { sessionStorage.removeItem(STASH_KEY); } catch {}
    }
    if (autoprint && data?.id) {
      // SEC-003: short-lived download token instead of the long access JWT.
      const token = await ensureDLToken().catch(() => "");
      const url = `${BACKEND_URL}/api/orders/${data.id}/label.pdf${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Label sent to printer tab");
    }
    navigate(ordersHref, { state: { newOrder: data } });
  };

  const initialNotes = roster?.delivery_notes
    ? roster.delivery_notes
    : prefill
    ? `Scanned: ${prefill}`
    : "";

  return (
    <div className="p-6 lg:p-10 max-w-2xl mx-auto" data-testid="admin-new-order-page">
      <header className="mb-6">
        <h1 className="text-3xl font-display font-bold tracking-tight">New delivery order</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Fill in customer details, items and delivery address.
        </p>
        {roster && (
          <div className="mt-3 p-3 rounded-xl bg-accent/10 border border-accent/30 text-sm" data-testid="roster-banner">
            <div className="font-semibold text-accent">Auto-filled from roster</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Order # <span className="font-mono">{roster.order_number}</span> — patient details below were pre-filled. Pick the Pharmacy, Deliver Type, Priority and Items, then Save.
            </div>
          </div>
        )}
        {prefill && !roster && (
          <p className="block mt-2 text-primary text-xs" data-testid="prefill-notice">
            Scanned code <span className="font-mono">{prefill}</span> has been added to Notes (not found in roster).
          </p>
        )}
        {autoprint && (
          <p className="block mt-1 text-accent text-xs" data-testid="autoprint-notice">
            4×6 label will print automatically after Save.
          </p>
        )}
      </header>
      <OrderForm
        pharmacies={pharmacies}
        allowPharmacySelect={true}
        defaultPharmacyId={roster?.default_pharmacy_id || undefined}
        initialDeliverType={roster?.default_deliver_type || "same_day"}
        initialPriority={roster?.default_priority || "normal"}
        initialPatientName={roster?.customer_name || ""}
        initialPatientPhone={roster?.customer_phone || ""}
        initialAddress={roster?.address || ""}
        initialNotes={initialNotes}
        onCreated={handleCreated}
        onCancel={() => {
          try { sessionStorage.removeItem(STASH_KEY); } catch {}
          navigate(ordersHref);
        }}
      />
    </div>
  );
}
